import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import using absolute URL from public folder
// import logo from '../assets/logo.png';

interface HeaderProps {
  className?: string;
}

type Language = {
  code: string;
  name: string;
  flag: string;
};

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { t, i18n } = useTranslation();
  // Create a state to force re-render when language changes
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const location = useLocation();
  const navigate = useNavigate();

  const languages: Language[] = [
    { code: 'en', name: 'ENGLISH', flag: '🇺🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'fr', name: 'FRANÇAIS', flag: '🇫🇷' },
    { code: 'es', name: 'ESPAÑOL', flag: '🇪🇸' },
  ];

  // Function to navigate to a section on the homepage
  const navigateToSection = (sectionId: string) => {
    const isHomePage = location.pathname === '/';

    if (isHomePage) {
      // If already on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on another page, navigate to homepage with the section hash
      navigate(`/#${sectionId}`);
    }

    // Close mobile menu if it's open
    if (isMenuOpen) {
      setIsMenuOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    // Handle hash in URL after navigation
    if (location.hash) {
      const id = location.hash.substring(1); // remove the # character
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add passive: true for better scroll performance
    window.addEventListener('scroll', onScroll, { passive: true });

    // Trigger initial check
    handleScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle escape key for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMenuOpen) {
          setIsMenuOpen(false);
          document.body.style.overflow = 'auto';
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  // Add listener for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(i18n.language);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = (code: string) => {
    console.log('Changing language to:', code);
    i18n.changeLanguage(code).then(() => {
      // Save selected language in localStorage for persistence
      localStorage.setItem('preferredLanguage', code);
      // Set HTML lang attribute
      document.documentElement.lang = code;
      // Force update the component state
      setCurrentLanguage(code);
    }).catch((err: Error) => {
      console.error('Failed to change language:', err);
    });
  };

  // Load saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
      document.documentElement.lang = savedLanguage;
      setCurrentLanguage(savedLanguage);
    }
  }, [i18n]);

  const headerHeight = isScrolled ? '60px' : '120px';

  return (
    <>
      <header
        className={`header ${isScrolled ? 'header--scrolled' : ''} ${className}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
          transition: 'all 0.3s ease'
        }}
      >
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem',
          width: '100%',
          position: 'relative' // Add relative positioning
        }}>
          <div className="header__content" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: headerHeight,
            transition: 'height 0.3s ease',
            position: 'relative'
          }}>
            {/* Logo area - adjust flex basis for mobile */}
            <div style={{
              flex: isMobile ? '0 0 200px' : '0 0 260px', // Reduce size on mobile
              minWidth: isMobile ? '180px' : '240px', // Reduce min-width on mobile
              marginRight: isMobile ? '10px' : '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
              paddingBottom: isScrolled ? '0' : '10px',
              transition: 'padding-bottom 0.3s ease',
              overflow: 'hidden'
            }}>
              {!isScrolled && (
                <Link to="/" className="header__logo" style={{
                  display: 'block',
                  height: '110px',
                  position: 'relative',
                  zIndex: 5,
                  top: '10px'
                }}>
                  <img
                    src="/logo.png"
                    alt="Airline Crew Trans"
                    style={{
                      height: '105px',
                      width: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      maxWidth: '100%',
                      marginTop: '0',
                      transform: 'scale(1.05)',
                      transformOrigin: 'left center'
                    }}
                  />
                </Link>
              )}
            </div>

            {/* Regular nav for desktop */}
            <nav className="header__nav" style={{
              flex: '1 1 auto',
              display: isMobile ? 'none' : 'flex',
              justifyContent: 'flex-end'
            }}>
              <ul className="header__nav-list" id="desktop-menu" role="menu" style={{
                display: 'flex',
                listStyle: 'none',
                padding: 0,
                margin: 0,
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <li className="header__nav-item" role="none">
                  <Link to="/" className="header__nav-link" role="menuitem" style={{
                    color: '#222',
                    fontWeight: 600,
                    textDecoration: 'none',
                    padding: '0.5rem 1rem',
                    transition: 'color 0.3s ease',
                    fontSize: isScrolled ? '0.9rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '44px'
                  }}>{t('home')}</Link>
                </li>
                <li className="header__nav-item" role="none">
                  <button
                    onClick={() => navigateToSection('about')}
                    className="header__nav-link"
                    role="menuitem"
                    style={{
                      color: '#222',
                      fontWeight: 600,
                      textDecoration: 'none',
                      padding: '0.5rem 1rem',
                      transition: 'color 0.3s ease',
                      fontSize: isScrolled ? '0.9rem' : '1rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      height: '44px'
                    }}
                  >
                    {t('about_us')}
                  </button>
                </li>
                <li className="header__nav-item" role="none">
                  <button
                    onClick={() => navigateToSection('services')}
                    className="header__nav-link"
                    role="menuitem"
                    style={{
                      color: '#222',
                      fontWeight: 600,
                      textDecoration: 'none',
                      padding: '0.5rem 1rem',
                      transition: 'color 0.3s ease',
                      fontSize: isScrolled ? '0.9rem' : '1rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      height: '44px'
                    }}
                  >
                    {t('services')}
                  </button>
                </li>
                <li className="header__nav-item" role="none">
                  <button
                    onClick={() => navigateToSection('contact')}
                    className="header__nav-link"
                    role="menuitem"
                    style={{
                      color: '#222',
                      fontWeight: 600,
                      textDecoration: 'none',
                      padding: '0.5rem 1rem',
                      transition: 'color 0.3s ease',
                      fontSize: isScrolled ? '0.9rem' : '1rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      height: '44px'
                    }}
                  >
                    {t('contact')}
                  </button>
                </li>
                <li className="header__nav-item" role="none">
                  <Link to="/our-locations" className="header__nav-link" role="menuitem" style={{
                    color: '#222',
                    fontWeight: 600,
                    textDecoration: 'none',
                    padding: '0.5rem 1rem',
                    transition: 'color 0.3s ease',
                    fontSize: isScrolled ? '0.9rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '44px'
                  }}>{t('locations')}</Link>
                </li>
              </ul>
            </nav>

            <div className="header__right" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: 'auto', // Push to the right
              position: 'relative', // Add relative positioning
              zIndex: 1500
            }}>
              <button
                className={`hamburger ${isMenuOpen ? 'hamburger--active' : ''}`}
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label="Toggle menu"
                aria-controls="mobile-menu"
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '10px',
                  display: isMobile ? 'flex' : 'none', // Change to flex
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1500,
                  width: '44px', // Fixed width
                  height: '44px', // Fixed height
                  marginRight: '0' // Remove margin
                }}
              >
                <span className="hamburger__line" style={{
                  display: 'block',
                  width: '24px',
                  height: '2px',
                  marginBottom: '5px',
                  backgroundColor: '#222',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
                }}></span>
                <span className="hamburger__line" style={{
                  display: 'block',
                  width: '24px',
                  height: '2px',
                  marginBottom: '5px',
                  backgroundColor: '#222',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1
                }}></span>
                <span className="hamburger__line" style={{
                  display: 'block',
                  width: '24px',
                  height: '2px',
                  backgroundColor: '#222',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
                }}></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Language bar - moved below mobile menu in hierarchy but visually still under header */}
      <div className="language-bar" style={{
        position: 'fixed',
        top: headerHeight,
        left: 0,
        width: '100%',
        zIndex: isMenuOpen ? 990 : 999, // Lower z-index when menu is open
        backgroundColor: 'var(--off-white)',
        borderBottom: '1px solid var(--light-gray)',
        transition: 'top 0.3s ease'
      }}>
        <div className="language-bar__container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '8px 1rem', display: 'flex', justifyContent: 'center' }}>
          <ul className="language-bar__options" role="radiogroup" aria-label="Select a language" style={{ display: 'flex', listStyle: 'none', padding: 0, margin: 0 }}>
            {languages.map((language) => (
              <li key={language.code} className="language-bar__option" style={{ margin: '0 15px' }}>
                <button
                  className={`language-bar__button ${currentLanguage === language.code ? 'language-bar__button--active' : ''}`}
                  onClick={() => changeLanguage(language.code)}
                  role="radio"
                  aria-checked={currentLanguage === language.code}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'center',
                    fontSize: 'var(--font-size-small)',
                    color: 'var(--dark-gray)',
                    background: 'none',
                    border: 'none',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontWeight: currentLanguage === language.code ? 'var(--font-weight-medium)' : 'normal',
                  }}
                >
                  <span className="language-bar__flag" style={{
                    marginRight: isMobile ? '0' : '8px',
                    marginBottom: isMobile ? '4px' : '0',
                    fontSize: isMobile ? '1.5rem' : 'inherit'
                  }}>
                    {language.flag}
                  </span>
                  {isMobile ?
                    <span style={{ fontSize: '0.7rem' }}>{language.code.toUpperCase()}</span> :
                    <span>{language.name}</span>
                  }
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Mobile menu overlay - moved after language bar in DOM but with higher z-index */}
      {isMenuOpen && (
        <div className="mobile-menu-overlay" style={{
          position: 'fixed',
          top: headerHeight,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1200, // Higher than language bar but lower than header
          overflowY: 'auto',
          padding: '2rem 1rem'
        }}>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: '40px 0 0 0', // Add top margin to push content below language bar
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem'
          }}>
            <li style={{ margin: '0.5rem 0', width: '45%', textAlign: 'center' }}>
              <Link to="/" style={{
                color: '#222',
                fontWeight: 600,
                fontSize: '1.2rem',
                textDecoration: 'none',
                display: 'block',
                padding: '0.5rem'
              }} onClick={() => setIsMenuOpen(false)}>
                {t('home')}
              </Link>
            </li>
            <li style={{ margin: '0.5rem 0', width: '45%', textAlign: 'center' }}>
              <button style={{
                color: '#222',
                fontWeight: 600,
                fontSize: '1.2rem',
                textDecoration: 'none',
                display: 'block',
                padding: '0.5rem',
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }} onClick={() => navigateToSection('about')}>
                {t('about_us')}
              </button>
            </li>
            <li style={{ margin: '0.5rem 0', width: '45%', textAlign: 'center' }}>
              <button style={{
                color: '#222',
                fontWeight: 600,
                fontSize: '1.2rem',
                textDecoration: 'none',
                display: 'block',
                padding: '0.5rem',
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }} onClick={() => navigateToSection('services')}>
                {t('services')}
              </button>
            </li>
            <li style={{ margin: '0.5rem 0', width: '45%', textAlign: 'center' }}>
              <button style={{
                color: '#222',
                fontWeight: 600,
                fontSize: '1.2rem',
                textDecoration: 'none',
                display: 'block',
                padding: '0.5rem',
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }} onClick={() => navigateToSection('contact')}>
                {t('contact')}
              </button>
            </li>
            <li style={{ margin: '0.5rem 0', width: '45%', textAlign: 'center' }}>
              <Link to="/our-locations" style={{
                color: '#222',
                fontWeight: 600,
                fontSize: '1.2rem',
                textDecoration: 'none',
                display: 'block',
                padding: '0.5rem'
              }} onClick={() => setIsMenuOpen(false)}>
                {t('locations')}
              </Link>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default Header; 